import { Box, Stack, Typography, SxProps } from '@mui/material';
import { Loading } from 'components/Loading';
import { Button } from 'system/atoms';
import { useEmptyStateColours } from './useEmptyStateColours';

export type EmptyStateType =
  | 'no-results'
  | 'no-records'
  | 'complete'
  | 'loading';

type EmptyStateProps = {
  type?: EmptyStateType;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  onButtonClick?: () => void;
};

const getDefaultContent = (type: EmptyStateType) => {
  switch (type) {
    case 'no-results':
      return {
        title: 'Nothing found',
        subtitle: 'Search for something else or adjust your filters'
      };
    case 'no-records':
      return {
        title: 'Nothing to show',
        subtitle: undefined
      };
    case 'complete':
      return {
        title: 'All issues resolved!',
        subtitle: undefined
      };
    case 'loading':
      return {
        title: 'Loading',
        subtitle: undefined
      };
  }
};

const containerSx: SxProps = {
  width: '100%',
  py: '60px'
};

const iconContainerSx: SxProps = {
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const loadingTextSx: SxProps = {
  textAlign: 'center'
};

const iconSx: SxProps = {
  fontSize: '28px'
};

const titleSx: SxProps = {
  fontSize: '24px',
  lineHeight: '32px',
  textAlign: 'center',
  mb: '20px'
};

const subtitleSx: SxProps = {
  lineHeight: '26px',
  textAlign: 'center'
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  type = 'no-results',
  title,
  subtitle,
  buttonText,
  onButtonClick
}) => {
  const defaultContent = getDefaultContent(type);
  const { IconComponent, iconBgColor, iconColor, textColor, containerBgColor } =
    useEmptyStateColours(type);

  const displayTitle = title ?? defaultContent.title;
  const displaySubtitle = subtitle ?? defaultContent.subtitle;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap="12px"
      sx={{
        ...containerSx,
        backgroundColor: containerBgColor
      }}
    >
      {type === 'loading' && (
        <Stack alignItems="center" gap="20px">
          <Loading size="64px" />
          <Typography
            variant="summary"
            sx={{
              ...loadingTextSx,
              color: textColor
            }}
          >
            {displayTitle}
          </Typography>
        </Stack>
      )}

      {type !== 'loading' && (
        <>
          <Box
            sx={{
              ...iconContainerSx,
              backgroundColor: iconBgColor
            }}
          >
            {IconComponent && (
              <IconComponent
                sx={{
                  ...iconSx,
                  color: iconColor
                }}
              />
            )}
          </Box>

          <Typography
            variant="heading"
            sx={{
              ...titleSx,
              color: textColor
            }}
          >
            {displayTitle}
          </Typography>

          {displaySubtitle && (
            <Typography
              variant="default"
              sx={{
                ...subtitleSx,
                color: textColor
              }}
            >
              {displaySubtitle}
            </Typography>
          )}

          {type === 'no-records' && buttonText && onButtonClick && (
            <Button onClick={onButtonClick}>{buttonText}</Button>
          )}
        </>
      )}
    </Stack>
  );
};
